import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { Bell, CheckCircle2, ExternalLink, Store, Clock, Save } from 'lucide-react';

const DAY_NAMES = [
  { id: 0, label: 'Seg' },
  { id: 1, label: 'Ter' },
  { id: 2, label: 'Qua' },
  { id: 3, label: 'Qui' },
  { id: 4, label: 'Sex' },
  { id: 5, label: 'Sáb' },
  { id: 6, label: 'Dom' },
];

const AdminSettings = () => {
  const { authAxios } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStore, setSavingStore] = useState(false);
  const [savingNotif, setSavingNotif] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await authAxios.get('/admin/settings');
      setSettings(response.data);
    } catch {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (loadingSetter) => {
    loadingSetter(true);
    try {
      await authAxios.put('/admin/settings', settings);
      toast.success('Configurações salvas!');
    } catch {
      toast.error('Erro ao salvar');
    } finally {
      loadingSetter(false);
    }
  };

  const handleTest = async () => {
    if (!settings.callmebot_phone || !settings.callmebot_apikey) {
      toast.error('Preencha telefone e API key primeiro e salve as configurações');
      return;
    }
    setTesting(true);
    try {
      const response = await authAxios.post('/admin/test-notification');
      toast.success(response.data.message || 'Mensagem enviada! Verifique seu WhatsApp.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar teste');
    } finally {
      setTesting(false);
    }
  };

  const toggleDay = (dayId) => {
    const current = settings.open_days || [];
    const next = current.includes(dayId) ? current.filter((d) => d !== dayId) : [...current, dayId].sort();
    setSettings({ ...settings, open_days: next });
  };

  if (loading || !settings) {
    return <div className="text-center py-10 text-[#5C4247]">Carregando...</div>;
  }

  return (
    <div className="space-y-8 max-w-3xl">
      {/* ================= STORE INFO ================= */}
      <section>
        <h2
          className="text-2xl font-bold text-[#291014] flex items-center gap-2 mb-1"
          style={{ fontFamily: 'Outfit, sans-serif' }}
          data-testid="store-settings-section-title"
        >
          <Store className="h-6 w-6 text-[#EA580C]" />
          Informações da Loja
        </h2>
        <p className="text-sm text-[#5C4247] mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Esses dados aparecem no site para seus clientes.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            saveSettings(setSavingStore);
          }}
          className="bg-white rounded-2xl p-6 border border-[#F3E8E3] space-y-4"
        >
          <div>
            <Label htmlFor="pix_key">Chave PIX</Label>
            <Input
              id="pix_key"
              value={settings.pix_key || ''}
              onChange={(e) => setSettings({ ...settings, pix_key: e.target.value })}
              placeholder="CPF, CNPJ, celular, email ou chave aleatória"
              className="mt-1 font-mono"
              data-testid="pix-key-input"
            />
            <p className="text-xs text-[#5C4247] mt-1">Chave usada para receber pagamentos PIX</p>
          </div>

          <div>
            <Label htmlFor="whatsapp">WhatsApp (com DDI + DDD)</Label>
            <Input
              id="whatsapp"
              value={settings.whatsapp || ''}
              onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value.replace(/\D/g, '') })}
              placeholder="5573991923111"
              className="mt-1 font-mono"
              data-testid="whatsapp-input"
            />
            <p className="text-xs text-[#5C4247] mt-1">Ex: 5573991923111 (código do país 55 + DDD + número)</p>
          </div>

          <div>
            <Label htmlFor="instagram">Instagram (apenas @)</Label>
            <Input
              id="instagram"
              value={settings.instagram || ''}
              onChange={(e) => setSettings({ ...settings, instagram: e.target.value.replace(/@/g, '').trim() })}
              placeholder="petiscariacamamu"
              className="mt-1"
              data-testid="instagram-input"
            />
          </div>

          <div>
            <Label htmlFor="address">Endereço</Label>
            <Input
              id="address"
              value={settings.address || ''}
              onChange={(e) => setSettings({ ...settings, address: e.target.value })}
              placeholder="Rua, nº, bairro, cidade"
              className="mt-1"
              data-testid="address-input"
            />
          </div>

          <div>
            <Label htmlFor="min_order">Pedido Mínimo (R$)</Label>
            <Input
              id="min_order"
              type="number"
              step="0.01"
              min="0"
              value={settings.min_order ?? 30}
              onChange={(e) => setSettings({ ...settings, min_order: parseFloat(e.target.value) || 0 })}
              className="mt-1"
              data-testid="min-order-input"
            />
          </div>

          {/* Operating Hours */}
          <div className="border-t border-[#F3E8E3] pt-4">
            <Label className="flex items-center gap-2 mb-3">
              <Clock className="h-4 w-4 text-[#EA580C]" />
              Horário de Funcionamento
            </Label>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="open-days-selector">
              {DAY_NAMES.map((d) => {
                const active = (settings.open_days || []).includes(d.id);
                return (
                  <button
                    type="button"
                    key={d.id}
                    onClick={() => toggleDay(d.id)}
                    className={`h-10 w-14 rounded-full text-sm font-semibold transition-all ${
                      active
                        ? 'bg-[#7F1D1D] text-white border-2 border-[#7F1D1D]'
                        : 'bg-white text-[#5C4247] border-2 border-[#F3E8E3] hover:border-[#7F1D1D]'
                    }`}
                    data-testid={`day-toggle-${d.id}`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="open_hour">Abre às (h)</Label>
                <Input
                  id="open_hour"
                  type="number"
                  min="0"
                  max="23"
                  value={settings.open_hour ?? 19}
                  onChange={(e) => setSettings({ ...settings, open_hour: parseInt(e.target.value, 10) || 0 })}
                  className="mt-1"
                  data-testid="open-hour-input"
                />
              </div>
              <div>
                <Label htmlFor="close_hour">Fecha às (h)</Label>
                <Input
                  id="close_hour"
                  type="number"
                  min="0"
                  max="23"
                  value={settings.close_hour ?? 22}
                  onChange={(e) => setSettings({ ...settings, close_hour: parseInt(e.target.value, 10) || 0 })}
                  className="mt-1"
                  data-testid="close-hour-input"
                />
              </div>
            </div>
            <p className="text-xs text-[#5C4247] mt-2">
              Use 24h: ex. abre 19h, fecha 22h. O banner "ABERTO/FECHADO" no site atualiza automaticamente.
            </p>
          </div>

          <Button
            type="submit"
            disabled={savingStore}
            className="w-full bg-[#7F1D1D] hover:bg-[#991B1B] text-white font-semibold py-6 rounded-full"
            data-testid="save-store-settings-button"
          >
            <Save className="h-4 w-4 mr-2" />
            {savingStore ? 'Salvando...' : 'Salvar Informações da Loja'}
          </Button>
        </form>
      </section>

      {/* ================= NOTIFICATIONS ================= */}
      <section>
        <h2
          className="text-2xl font-bold text-[#291014] flex items-center gap-2 mb-1"
          style={{ fontFamily: 'Outfit, sans-serif' }}
          data-testid="notif-settings-section-title"
        >
          <Bell className="h-6 w-6 text-[#EA580C]" />
          Notificações no WhatsApp
        </h2>
        <p className="text-sm text-[#5C4247] mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Receba automaticamente uma mensagem no seu WhatsApp quando um cliente fizer um pedido novo.
        </p>

        <div className="bg-gradient-to-br from-[#25D366]/10 to-[#EA580C]/10 border border-[#25D366]/30 rounded-2xl p-5 mb-4">
          <h3
            className="font-bold text-[#291014] mb-3 flex items-center gap-2"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            📋 Como configurar (leva 2 minutos)
          </h3>
          <ol className="space-y-2 text-sm text-[#291014]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <li>
              <strong>1.</strong> Adicione este número aos seus contatos:{' '}
              <a
                href="https://wa.me/34644519523"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#7F1D1D] font-bold underline inline-flex items-center gap-1"
                data-testid="callmebot-number-link"
              >
                +34 644 51 95 23 <ExternalLink className="h-3 w-3" />
              </a>
            </li>
            <li>
              <strong>2.</strong> Envie a mensagem exata:{' '}
              <code className="bg-white px-2 py-1 rounded font-mono text-xs">I allow callmebot to send me messages</code>
            </li>
            <li>
              <strong>3.</strong> Você vai receber uma resposta com sua <strong>API key</strong>.
            </li>
            <li>
              <strong>4.</strong> Cole os dados abaixo e clique em <strong>Salvar</strong>. Depois teste!
            </li>
          </ol>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            saveSettings(setSavingNotif);
          }}
          className="bg-white rounded-2xl p-6 border border-[#F3E8E3] space-y-4"
        >
          <div className="flex items-center justify-between bg-[#FAF8F5] rounded-lg p-4">
            <div>
              <Label className="text-base font-semibold">Ativar notificações</Label>
              <p className="text-xs text-[#5C4247] mt-1">Envia msg no seu WhatsApp a cada pedido</p>
            </div>
            <Switch
              checked={settings.callmebot_enabled || false}
              onCheckedChange={(v) => setSettings({ ...settings, callmebot_enabled: v })}
              data-testid="notifications-enabled-toggle"
            />
          </div>

          <div>
            <Label htmlFor="callmebot_phone">Seu WhatsApp *</Label>
            <Input
              id="callmebot_phone"
              value={settings.callmebot_phone || ''}
              onChange={(e) => setSettings({ ...settings, callmebot_phone: e.target.value.replace(/\D/g, '') })}
              placeholder="5573991923111"
              className="mt-1 font-mono"
              data-testid="callmebot-phone-input"
            />
            <p className="text-xs text-[#5C4247] mt-1">Com código do país (55) + DDD + número</p>
          </div>

          <div>
            <Label htmlFor="callmebot_apikey">API Key CallMeBot *</Label>
            <Input
              id="callmebot_apikey"
              value={settings.callmebot_apikey || ''}
              onChange={(e) => setSettings({ ...settings, callmebot_apikey: e.target.value.trim() })}
              placeholder="123456"
              className="mt-1 font-mono"
              data-testid="callmebot-apikey-input"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              type="submit"
              disabled={savingNotif}
              className="flex-1 bg-[#7F1D1D] hover:bg-[#991B1B] text-white font-semibold py-6 rounded-full"
              data-testid="save-notif-settings-button"
            >
              {savingNotif ? 'Salvando...' : 'Salvar Notificações'}
            </Button>
            <Button
              type="button"
              onClick={handleTest}
              disabled={testing || !settings.callmebot_phone || !settings.callmebot_apikey}
              variant="outline"
              className="flex-1 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white font-semibold py-6 rounded-full"
              data-testid="test-notification-button"
            >
              {testing ? 'Enviando...' : '📱 Enviar teste'}
            </Button>
          </div>
        </form>

        {settings.callmebot_enabled && settings.callmebot_phone && settings.callmebot_apikey && (
          <div
            className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3 mt-4"
            data-testid="notifications-active-banner"
          >
            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-green-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Notificações ativadas!
              </p>
              <p className="text-sm text-green-800 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                A partir de agora, cada pedido novo enviará automaticamente uma mensagem no seu WhatsApp.
              </p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminSettings;
